import React, { useState } from 'react';
import { X, User, Mail, Phone, Lock, LogIn, UserPlus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import WelcomeModal from './WelcomeModal';
import ForgotPassword from '../ForgotPassword';

const API = '/api';

const AuthModal = ({ isOpen, onClose, onSuccess, defaultMode }) => {
  const [isLogin, setIsLogin] = useState(defaultMode !== 'signup');
  const [loading, setLoading] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [stayLoggedIn, setStayLoggedIn] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLogin && !agreedToTerms) {
      toast.error('Please agree to the Terms of Service, Privacy Policy, and SMS consent to create an account.');
      return;
    }
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : {
            ...formData,
            sms_consent: true,
            sms_consent_text: 'I agree to the Terms of Service and Privacy Policy and consent to receive booking confirmation, approval, and appointment reminder text messages from High Quality Studios. Msg & data rates may apply. Reply STOP to opt out.',
            sms_consent_timestamp: new Date().toISOString(),
            terms_agreed: true
          };

      console.log('Login attempt:', { 
        endpoint: `${API}${endpoint}`, 
        email: payload.email,
        passwordLength: payload.password?.length 
      });

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      // Store token and user data
      localStorage.setItem('auth_token', response.data.token);
      localStorage.setItem('user_data', JSON.stringify(response.data.user));
      localStorage.setItem('user_email', response.data.user.email);
      
      // Store stay logged in preference
      if (stayLoggedIn) {
        localStorage.setItem('stay_logged_in', 'true');
      } else {
        localStorage.removeItem('stay_logged_in');
      }
      
      toast.success(response.data.message);
      onSuccess(response.data.user);
      
      // Show welcome modal for new signups BEFORE closing
      if (!isLogin) {
        setNewUserName(response.data.user.name);
        setShowWelcome(true);
        // Don't close auth modal yet - welcome modal will close it
      } else {
        // For login, close immediately
        onClose();
      }
      
      // Reset form
      setFormData({ name: '', email: '', phone: '', password: '' });
    } catch (error) {
      console.error('Auth error:', error);
      console.error('Error details:', error.response);
      const errorMsg = error.response?.data?.detail || error.message || 'Authentication failed';
      
      // Handle PASSWORD_RESET_REQUIRED - prompt user to reset their password
      if (errorMsg === 'PASSWORD_RESET_REQUIRED') {
        toast.error('Your password needs to be reset. Please click "Forgot Password?" to set a new password.');
        setShowForgotPassword(true);
      } else {
        console.log('Showing error:', errorMsg);
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed bg-black/70 backdrop-blur-sm overflow-y-auto" 
      style={{ 
        zIndex: 9999,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        position: 'fixed'
      }}
    >
      <div className="min-h-screen flex items-center justify-center p-4 py-8">
        <div className="relative bg-white border-4 border-purple-600 rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl shadow-purple-500/50">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            {isLogin ? <LogIn size={24} className="text-purple-600" /> : <UserPlus size={24} className="text-purple-600" />}
            {isLogin ? 'Login' : 'Create Account'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-900 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required={!isLogin}
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border-2 border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-purple-600 focus:ring-2 focus:ring-purple-200"
                    placeholder="John Doe"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required={!isLogin}
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border-2 border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-purple-600 focus:ring-2 focus:ring-purple-200"
                    placeholder="555-1234"
                  />
                </div>
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full pl-10 pr-4 py-3 bg-gray-50 border-2 border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-purple-600 focus:ring-2 focus:ring-purple-200"
                placeholder="you@example.com"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Password
              </label>
              {isLogin && (
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                >
                  Forgot Password?
                </button>
              )}
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                minLength={6}
                className="w-full pl-10 pr-4 py-3 bg-gray-50 border-2 border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-purple-600 focus:ring-2 focus:ring-purple-200"
                placeholder="••••••••"
              />
            </div>
            {!isLogin && (
              <p className="text-xs text-gray-600 mt-1">Minimum 6 characters</p>
            )}
          </div>

          {/* Stay Logged In Checkbox - Only for Login */}
          {isLogin && (
            <div className="flex items-center">
              <input
                type="checkbox"
                id="stayLoggedIn"
                checked={stayLoggedIn}
                onChange={(e) => setStayLoggedIn(e.target.checked)}
                className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
              />
              <label htmlFor="stayLoggedIn" className="ml-2 text-sm text-gray-700 cursor-pointer">
                Stay logged in on this device
              </label>
            </div>
          )}

          {/* Terms + SMS consent — signup only (TCPA / Twilio A2P compliance) */}
          {!isLogin && (
            <label htmlFor="signup-terms-sms" className="flex items-start gap-2 p-3 bg-purple-50 border border-purple-200 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                id="signup-terms-sms"
                data-testid="signup-terms-sms-checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-purple-600 cursor-pointer flex-shrink-0"
              />
              <span className="text-xs text-gray-700 leading-relaxed">
                I agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-purple-700 underline font-semibold">Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-purple-700 underline font-semibold">Privacy Policy</a>
                , and I consent to receive booking confirmation, approval, and appointment reminder text messages from High Quality Studios. Msg &amp; data rates may apply. Reply <span className="font-mono bg-purple-100 px-1 rounded">STOP</span> to opt out.
              </span>
            </label>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Please wait...' : isLogin ? 'Login' : 'Create Account'}
          </button>
        </form>

        {/* Toggle */}
        <div className="mt-6 text-center">
          <p className="text-gray-600 text-sm">
            {isLogin ? "Don't have an account?" : 'Already have an account?'}
            {' '}
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setFormData({ name: '', email: '', phone: '', password: '' });
              }}
              className="text-purple-600 hover:text-purple-700 font-semibold"
            >
              {isLogin ? 'Sign up' : 'Login'}
            </button>
          </p>
        </div>
        </div>
      </div>
      
      {/* Welcome/Onboarding Modal */}
      <WelcomeModal 
        isOpen={showWelcome}
        onClose={() => {
          setShowWelcome(false);
          onClose(); // Close auth modal too
        }}
        userName={newUserName}
      />
      
      {/* Forgot Password Modal */}
      <ForgotPassword 
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />
    </div>
  );
};

export default AuthModal;
